import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { TechsStore } from '../core/stores/techs.store';
import { Ecosystem, Tag, TechDetail } from '../models/models';
import { TechService } from '../services/tech.service';
import { SimilarTechsComponent } from '../similar-techs/similar-techs.component';
import { TagSelectorComponent } from '../tag-selector/tag-selector.component';
import { TagComponent } from '../tag/tag.component';
import { withMethods } from '@ngrx/signals';

@Component({
  selector: 'app-tech-detail',
  templateUrl: './tech-detail.component.html',
  styleUrls: ['./tech-detail.component.css'],
  standalone: true,
  imports: [TagComponent, SimilarTechsComponent, TagSelectorComponent],
})
export class TechDetailComponent implements OnInit, OnDestroy {

  private techService = inject(TechService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private ngUnsubscribe = new Subject<void>();

  readonly techsStore = inject(TechsStore);

  techEcoIds = signal<string[] | null>(null)
  techTagIds = signal<string[] | null>(null)
  nextSlug = signal<string | null>(null)
  previousSlug = signal<string | null>(null)
  slug = signal<string | undefined>(undefined);


  tech?: TechDetail | null;
  isArchived?: boolean;

  ngOnInit(): void {
    this.loadTech();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  get sortedTags() {
    return this.tech?.tags?.sort((tag1, tag2) => ((tag1.tagGenre?.id || "-").localeCompare(tag2.tagGenre?.id || "-"))) || []
  }

  async onEditTech() {
    await this.router.navigate([`tech/edit/${this.slug()}`]);
  }

  async onNextTech() {
    await this.router.navigate([`tech/${this.nextSlug()}`]);
  }

  async onPreviousTech() {
    await this.router.navigate([`tech/${this.previousSlug()}`]);
  }

  async onDeleteTech() {
    const confirmed = confirm("Are you sure you want to permanently delete this technology?");
    if (confirmed) {
      const confirmed2 = confirm("This action is irreversible, are you SURE?");
      if (confirmed2) {
        this.executeDeleteTech();
      }
    }
  }

  saveTags({ ecosystems, tags }: { ecosystems: Ecosystem[], tags: Tag[] }) {
    if (!this.tech || !this.tech.id) return
    this.techService.putTech(this.tech.id, { ...this.tech, ecosystemIds: ecosystems.map(e => e.id || ''), tagIds: tags.map(t => t.id || '') })
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (!this.tech) return
          this.loadTech();
        },
        error: (e) => console.error(e)
      })
  }

  async executeDeleteTech() {
    if (this.tech?.id) {
      this.techService.deleteTech(this.tech.id).subscribe();
      await this.router.navigate(['/techs']);
    } else {
      alert('No Current Tech');
    }
  }

  async onArchiveTech() {
    if (this.tech?.id) {
      this.techService.archiveTech(this.tech.id).subscribe();
      await this.router.navigate(['/tech']);
    } else {
      alert('No Current Tech');
    }
  }

  async back(): Promise<void> {
    await this.router.navigate([".."]);
  }

  async loadTech() {
    this.route.params.pipe(
      switchMap((params) => {
        const slug = params['slug'] as string;
        return slug ? this.techService.getTechDetailBySlug(slug).pipe(
          take(1),
          map(tech => ({ slug, tech }))
        ) : of({ slug, tech: null });
      }),
      takeUntil(this.ngUnsubscribe)
    ).subscribe({
      next: ({ slug, tech }) => {
        this.slug.set(slug);
        this.tech = tech?.data;

        this.techEcoIds.set(tech?.data?.ecosystems?.map(e => e.id || '') || null)
        this.techTagIds.set(tech?.data?.tags?.map(t => t.id || '') || null)

        const { nextSlug, previousSlug } = this.techsStore.getNextAndPreviousTechSlug(slug)
        this.nextSlug.set(nextSlug)
        this.previousSlug.set(previousSlug)

      },
      error: (e) => { }
    });
  }

  tagsChanged(newTags: Tag[]) {
    this.techTagIds.set(newTags.map(t => t.id || ''))
  }

  ecosystemsChanged(newTags: Ecosystem[]) {
    this.techEcoIds.set(newTags.map(t => t.id || ''))
  }
}
